// The one place a SCREEN turns the store's state into the `signer` that
// `courses-devices` takes (spec 6.6). Two containers need one — the recovery
// screen writes a signed `PUT /recovery`, the recovery-code entry screen
// registers or approves this device — and neither should be assembling four
// getters and a key-unwrap of its own.
//
// It reads MK, but it does not hand it back: `createOperationSigner` unwraps
// the bundle signing key once and closes over it, so what comes out is a
// function that signs and nothing that holds a key. A page may keep the result
// for as long as it needs to; it may not keep what went in.
import { createOperationSigner } from './signer';

/**
 * @param {object} store the root Vuex store
 * @returns {Promise<(nonce: string, operation: { op: string, params?: object }) => Promise<string>>}
 * @throws {Error} when this device holds no key, or the member's bundle has not
 *   been read yet — both of which are a caller ordering bug, not a member's
 *   mistake, so they fail loudly rather than reaching the API as a 403.
 */
export async function createSignerFromStore(store) {
  const material = store.getters['courses-keys/keyMaterial'];
  const signingKeyWrapped = store.getters['courses-devices/signingKeyWrapped'];
  const keyId = store.getters['courses-devices/keyId'];
  const memberId = store.getters['courses-keys/memberId'];

  if (!material?.mk) {
    throw new Error('createSignerFromStore: this device holds no master key');
  }
  if (!signingKeyWrapped || !keyId) {
    throw new Error('createSignerFromStore: the encryption status has not been read yet');
  }

  return createOperationSigner({ signingKeyWrapped, mk: material.mk, keyId, memberId });
}

/**
 * A signer over the PENDING generation, for `rotate/commit` and
 * `rotate/abort` (#1694, api#1135).
 *
 * WHY A SECOND ONE AT ALL. Both of those are signed by the pending bundle key,
 * not the current one, because what they must prove is possession of the
 * generation being committed or dropped. `#1130` taught the API's guard to
 * verify against it; before that no honest commit could have been accepted.
 *
 * The machinery is the same function with different arguments: the pending
 * signing key is wrapped under MK2, with the AAD bound to `pendingKeyId`, so
 * handing `createOperationSigner` the pending material is the whole of it.
 * Using the CURRENT `keyId` here would fail the AAD check and surface as
 * `AUTH_FAILED` — a local error, which is the right place for it, rather than
 * a 403 the member cannot act on.
 *
 * @param {object} store the root Vuex store
 * @returns {Promise<(nonce: string, operation: { op: string, params?: object }) => Promise<string>>}
 * @throws {Error} when this device is not the one holding the pending
 *   generation, or the status has not been read — both caller ordering bugs.
 */
export async function createPendingSignerFromStore(store) {
  const pending = store.getters['courses-keys/pendingKeyMaterial'];
  const pendingSigningKeyWrapped = store.getters['courses-devices/pendingSigningKeyWrapped'];
  const memberId = store.getters['courses-keys/memberId'];

  if (!pending?.mk) {
    // A device that never held MK2, or one that has already promoted or
    // dropped it. Either way it cannot finish somebody else's rotation, and
    // saying so here beats a signature the server refuses.
    throw new Error('createPendingSignerFromStore: this device holds no pending generation');
  }
  if (!pendingSigningKeyWrapped) {
    throw new Error('createPendingSignerFromStore: no rotation is in progress on the server');
  }

  return createOperationSigner({
    signingKeyWrapped: pendingSigningKeyWrapped,
    mk: pending.mk,
    keyId: pending.keyId,
    memberId,
  });
}
